import React, { useState } from "react";

export default function Contact({ language = "en", darkMode = false }) {
  const [form, setForm] = useState({
    companyName: "",
    email: "",
    phone: "",
    location: "",
    vertical: "",
    solution: "",
    altPhone: "",
    employees: "",
    about: "",
  });
  const [feedback, setFeedback] = useState("");
  const [errors, setErrors] = useState({});

  const translations = {
    en: {
      title: "Let’s Work Together",
      subtitle: "Tell us about your project or inquiry.",
      placeholders: {
        companyName: "Company Name",
        email: "Email",
        phone: "Contact Number",
        location: "Location",
        vertical: "Company Vertical",
        solution: "Solution Looking For",
        altPhone: "Alternate Phone (Optional)",
        employees: "Number of Employees (Optional)",
        about: "About Company (Optional)",
      },
      button: "Send Message",
      feedback: { success: "Message sent successfully!", error: "Error sending message." },
    },
    ar: {
      title: "لنعمل معًا",
      subtitle: "أخبرنا عن مشروعك أو استفسارك.",
      placeholders: {
        companyName: "اسم الشركة",
        email: "البريد الإلكتروني",
        phone: "رقم الاتصال",
        location: "الموقع",
        vertical: "قطاع الشركة",
        solution: "الحل المطلوب",
        altPhone: "الهاتف البديل (اختياري)",
        employees: "عدد الموظفين (اختياري)",
        about: "عن الشركة (اختياري)",
      },
      button: "إرسال الرسالة",
      feedback: { success: "تم إرسال الرسالة بنجاح!", error: "خطأ في إرسال الرسالة." },
    },
  };

  const currentLang = translations[language] || translations.en;

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case "companyName":
        if (!value) return "Company name is required.";
        break;
      case "email":
        if (!value) return "Email is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format.";
        break;
      case "phone":
        if (!value) return "Contact number is required.";
        if (!/^\d{7,15}$/.test(value)) return "Phone must be digits (7–15 numbers).";
        break;
      case "altPhone":
        if (value && !/^\d{7,15}$/.test(value)) return "Alternate phone must be digits (7–15 numbers).";
        break;
      case "location":
        if (!value) return "Location is required.";
        break;
      case "vertical":
        if (!value) return "Company vertical is required.";
        break;
      case "solution":
        if (!value) return "Solution is required.";
        break;
      case "employees":
        if (value && (isNaN(value) || value < 1)) return "Employees must be a positive number.";
        break;
      default:
        return "";
    }
    return "";
  };

  // Handle change with live validation
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // For phone fields, allow only digits
    if (name === "phone" || name === "altPhone") {
      newValue = value.replace(/\D/g, "");
    }

    setForm({ ...form, [name]: newValue });
    const error = validateField(name, newValue);
    setErrors({ ...errors, [name]: error });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Final validation before submit
    let newErrors = {};
    Object.keys(form).forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setFeedback("Sending...");
    try {
      const res = await fetch("https://infynixsolution.com/backend/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      setFeedback(res.ok ? data.message : data.error || currentLang.feedback.error);

      if (res.ok) {
        setForm({
          companyName: "",
          email: "",
          phone: "",
          location: "",
          vertical: "",
          solution: "",
          altPhone: "",
          employees: "",
          about: "",
        });
        setErrors({});
      }
    } catch (err) {
      setFeedback("Error sending message. " + err.message);
    }
  };

  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "50px 20px",
        color: "#fff",
        background: darkMode
          ? "linear-gradient(135deg, #0a0f2c, dodgerblue)"
          : "linear-gradient(135deg, #0a0f2c, #00bfff)",
      }}
    >
      <style>
        {`
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
        ::placeholder {
          color: #fff !important;
          opacity: 1 !important;
        }
        textarea::placeholder {
          color: #fff !important;
          opacity: 1 !important;
        }
        `}
      </style>

      <div
        style={{
          padding: "40px",
          borderRadius: "16px",
          maxWidth: "600px",
          width: "100%",
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          backgroundColor: darkMode ? "rgba(30, 58, 138, 0.1)" : "rgba(255, 255, 255, 0.05)",
        }}
      >
        <h2 style={titleStyle(language)}>{currentLang.title}</h2>
        <p style={subtitleStyle(language)}>{currentLang.subtitle}</p>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {/* Required */}
          <Field name="companyName" placeholder={currentLang.placeholders.companyName} value={form.companyName} onChange={handleChange} required language={language} error={errors.companyName} />
          <Field type="email" name="email" placeholder={currentLang.placeholders.email} value={form.email} onChange={handleChange} required language={language} error={errors.email} />
          <Field name="phone" placeholder={currentLang.placeholders.phone} value={form.phone} onChange={handleChange} required language={language} error={errors.phone} inputMode="numeric" />
          <Field name="location" placeholder={currentLang.placeholders.location} value={form.location} onChange={handleChange} required language={language} error={errors.location} />
          <Field name="vertical" placeholder={currentLang.placeholders.vertical} value={form.vertical} onChange={handleChange} required language={language} error={errors.vertical} />
          <Field name="solution" placeholder={currentLang.placeholders.solution} value={form.solution} onChange={handleChange} required language={language} error={errors.solution} />

          {/* Optional */}
          <Field name="altPhone" placeholder={currentLang.placeholders.altPhone} value={form.altPhone} onChange={handleChange} language={language} error={errors.altPhone} inputMode="numeric" />

          <Field type="number" name="employees" placeholder={currentLang.placeholders.employees} value={form.employees} onChange={handleChange} language={language} error={errors.employees} />

          <textarea
            name="about"
            placeholder={currentLang.placeholders.about}
            value={form.about}
            onChange={handleChange}
            rows="4"
            style={textareaStyle(language)}
          />

          <button type="submit" style={buttonStyle(language)}>{currentLang.button}</button>
          {feedback && <p style={feedbackStyle(language)}>{feedback}</p>}
        </form>
      </div>
    </div>
  );
}

// Input field component
const Field = ({ name, type = "text", placeholder, value, onChange, required, language, error, inputMode }) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    <input
      type={type}
      name={name}
      placeholder={placeholder + (required ? " *" : "")}
      value={value}
      onChange={onChange}
      required={required}
      style={inputStyle(language)}
      inputMode={inputMode}
      pattern={inputMode === "numeric" ? "[0-9]*" : undefined}
    />
    {error && <span style={{ color: "salmon", fontSize: "0.85rem", marginTop: "4px" }}>{error}</span>}
  </div>
);

// Styles
const inputStyle = (language) => ({
  background: "rgba(255, 255, 255, 0.1)",
  border: "1px solid #fff",
  borderRadius: "8px",
  padding: "14px",
  fontSize: "1rem",
  color: "#fff",
  outline: "none",
  fontFamily: language === "ar" ? "'Amiri', sans-serif" : "'Open Sans', sans-serif",
});

const textareaStyle = (language) => ({ ...inputStyle(language), resize: "vertical" });
const buttonStyle = (language) => ({
  background: "#00bfff",
  color: "#fff",
  padding: "14px",
  border: "none",
  borderRadius: "8px",
  fontWeight: "bold",
  fontSize: "1rem",
  cursor: "pointer",
  fontFamily: language === "ar" ? "'Amiri', sans-serif" : "'Open Sans', sans-serif",
});
const feedbackStyle = (language) => ({
  marginTop: "10px",
  textAlign: "center",
  fontSize: "0.95rem",
  color: "#e6f7ff",
  fontFamily: language === "ar" ? "'Amiri', sans-serif" : "'Open Sans', sans-serif",
});
const titleStyle = (language) => ({
  fontSize: "2.2rem",
  marginBottom: "10px",
  fontWeight: 600,
  textAlign: "center",
  fontFamily: language === "ar" ? "'Amiri', sans-serif" : "'Open Sans', sans-serif",
});
const subtitleStyle = (language) => ({
  fontSize: "1.1rem",
  marginBottom: "30px",
  textAlign: "center",
  opacity: 0.9,
  fontFamily: language === "ar" ? "'Amiri', sans-serif" : "'Open Sans', sans-serif",
});
